<template>
  <div
    class="short-video-wrapper"
    @wheel.prevent="handleWheel"
    @touchstart="touchStart"
    @touchmove.prevent
    @touchend="touchEnd"
  >
    <!-- 背景虚化模糊图 -->
    <transition name="el-fade-in">
      <div v-if="video" :key="video.videoId" class="video-bg" :style="{ backgroundImage: `url(${video.coverUrl})` }" />
    </transition>

    <!-- 视频核心播放区 -->
    <div class="video-container" @click="handleSingleClick" @dblclick="handleDoubleLike">
      <video
        v-if="videoActive && currentVideoUrl"
        ref="videoPlayer"
        class="native-video"
        :src="currentVideoUrl"
        loop
        autoplay
        preload="auto"
        playsinline
        webkit-playsinline
        x5-video-player-type="h5-page"
        x5-video-player-fullscreen="true"
        @loadedmetadata="onVideoLoad"
        @waiting="onWaiting"
        @playing="onPlaying"
        @error="onVideoError"
      />

      <!-- 加载及暂停状态提示 -->
      <div class="video-status-overlay">
        <transition name="el-zoom-in-center">
          <i v-if="isLoading" class="el-icon-loading status-icon" />
          <i v-else-if="isPaused" class="el-icon-video-play status-icon" />
        </transition>
      </div>

      <!-- 双击点赞爱心动画容器 -->
      <div ref="likeAnimBox" class="like-anim-container" />
    </div>

    <!-- 右侧悬浮功能交互栏 -->
    <div v-if="video" class="side-bar">
      <div v-if="user" class="side-item author-box" @click.stop="handleAvatarClick">
        <div class="avatar-wrapper">
          <el-avatar :size="48" :src="user.avatarUrl" />
          <div class="follow-plus"><i class="el-icon-plus" /></div>
        </div>
      </div>

      <div class="side-item" @click.stop="handleLike">
        <div class="icon-circle" :class="{ 'is-active': isLiked }"><i class="el-icon-star-on" /></div>
        <span class="count-text">{{ formatCount(video.view) }}</span>
      </div>
      <div class="side-item" @click.stop="toggleComments">
        <div class="icon-circle"><i class="el-icon-s-comment" /></div>
        <span class="count-text">{{ formatCount(video.comment) }}</span>
      </div>
      <div class="side-item" @click.stop="handleShare">
        <div class="icon-circle"><i class="el-icon-share" /></div>
        <span class="count-text">分享</span>
      </div>
    </div>

    <!-- 底部视频文案及音频信息 -->
    <div v-if="video" class="video-info-bottom">
      <div class="info-content">
        <h3 v-if="user" class="author-name" @click.stop="handleAvatarClick">@{{ user.screenName }}</h3>
        <p class="video-desc">{{ video.title }}</p>
        <div class="music-tag">
          <div class="music-icon-box"><i class="el-icon-headset" /></div>
          <div class="marquee-box">
            <span class="marquee-text">{{ video.title }} - 热门原声</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 顶部切换按钮 (已移除返回按钮) -->
    <div class="top-controls">
      <el-button type="info" icon="el-icon-refresh" circle size="small" :loading="isSwitching" @click="nextVideo" />
    </div>

    <!-- 作者详细资料右侧抽屉弹窗 -->
    <transition name="slide-fade">
      <div v-if="showUserCard" class="user-peek-card">
        <div class="card-mask" @click="showUserCard = false" />
        <div class="card-content">
          <div class="profile-main">
            <el-avatar
              :size="72"
              :src="user.avatarUrl"
              class="card-avatar clickable-avatar"
              @click.native="goToFullProfile"
            />
            <h2 class="name" @click="goToFullProfile">@{{ user.screenName }}</h2>
            <p class="bio">{{ user.signature || '这个人很懒，什么都没写~' }}</p>

            <div class="stats-group">
              <div class="stat"><b>{{ formatCount(user.fans) }}</b><span>粉丝</span></div>
              <div class="stat"><b>{{ formatCount(user.likes) }}</b><span>获赞</span></div>
            </div>

            <el-button type="danger" round class="action-btn" size="small" @click="handleFollowerUser">立即关注</el-button>
          </div>

          <div class="recent-works">
            <p class="label">近期作品</p>
            <div class="thumb-list">
              <div
                v-for="(item, index) in userWorks"
                :key="item.videoId || index"
                class="work-thumb"
                :style="{ backgroundImage: `url(${item.coverUrl})` }"
                @click="playThisWork(item)"
              >
                <i class="el-icon-video-play" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { videoUrl, getShortVideo, getRecentShortVideos } from '@/api/vod'
import { doAlbumAction } from '@/api/vod'

export default {
  name: 'ShortVideoMobile',
  data() {
    return {
      video: null,
      user: null,
      currentVideoUrl: '',
      videoActive: true,
      isLiked: false,
      isPaused: false,
      isLoading: true,
      isSwitching: false,
      isChangingSource: false,
      startY: 0,
      clickTimer: null,
      showUserCard: false,
      originalVolume: 1.0,
      volumeTimer: null,
      userWorks: []
    }
  },
  watch: {
    showUserCard(val) {
      if (val) {
        this.fadeVolume(0.2)
      } else {
        this.fadeVolume(this.originalVolume)
      }
    }
  },
  created() {
    document.title = 'Short'
    this.getShortVideoWrapper()
  },
  methods: {
    fadeVolume(targetVolume) {
      const v = this.$refs.videoPlayer
      if (!v) return

      clearInterval(this.volumeTimer)
      const step = v.volume < targetVolume ? 0.05 : -0.05

      this.volumeTimer = setInterval(() => {
        const newVolume = v.volume + step
        if ((step > 0 && newVolume >= targetVolume) || (step < 0 && newVolume <= targetVolume)) {
          v.volume = targetVolume
          clearInterval(this.volumeTimer)
        } else {
          v.volume = Math.max(0, Math.min(1, newVolume))
        }
      }, 30)
    },
    async handleAvatarClick() {
      this.showUserCard = true
      if (this.userWorks.length > 0 && this.userWorks[0].user && this.userWorks[0].user.userId === this.user.userId) {
        return
      }
      const queryParams = { userIdStr: this.user.userId }
      getRecentShortVideos(queryParams).then(resp => {
        if (resp.code === 0) {
          this.userWorks = resp.data || []
        } else {
          this.$message.warning((resp && resp.msg) || '获取作品失败')
        }
      })
    },
    playThisWork(work) {
      this.showUserCard = false
      this.prepareNewVideo(work.videoId)
    },
    goToFullProfile() {
      this.showUserCard = false
      this.fadeVolume(this.originalVolume || 1.0)
      this.$router.push(`/user/${this.user.userId}`).catch(err => {
        if (err.name !== 'NavigationDuplicated') console.error(err)
      })
    },
    handleLike() {
      const jsonData = {
        albumId: 1,
        postId: this.video.videoId,
        action: !this.isLiked ? 1 : 2
      }
      doAlbumAction(jsonData).then(resp => {
        if (resp.code === 0) {
          this.isLiked = !this.isLiked
          this.$message.success(resp.msg || '操作成功')
        } else {
          this.$message.warning((resp && resp.msg) || '操作失败')
        }
      })
    },
    handleShare() {
      this.$message.success('分享链接已复制')
    },
    toggleComments() {
      this.$message.info('评论功能开发中')
    },
    handleFollowerUser() {
      this.$message.info('关注成功')
    },
    async getShortVideoWrapper() {
      if (this.isSwitching) return
      this.isSwitching = true
      this.isLoading = true

      try {
        const resp = await getShortVideo()
        if (resp && resp.code === 0 && resp.data) {
          this.video = resp.data
          this.isLiked = false
          this.user = resp.data.user
          await this.prepareNewVideo(this.video.videoId)
        }
      } catch (err) {
        console.error(err)
        this.isSwitching = false
      }
    },
    async prepareNewVideo(videoId) {
      const resp = await videoUrl(videoId)
      if (resp && resp.code === 0) {
        const urls = resp.data.urls
        if (urls.length === 0) {
          this.$message.error('没有视频资源 URL')
          return
        }

        const nextUrl = urls[0].url
        this.isChangingSource = true
        this.videoActive = false

        const v = this.$refs.videoPlayer
        if (v) {
          v.pause()
          v.removeAttribute('src')
          v.load()
        }

        this.currentVideoUrl = ''

        setTimeout(() => {
          this.currentVideoUrl = nextUrl
          this.videoActive = true

          this.$nextTick(() => {
            const newV = this.$refs.videoPlayer
            if (newV) {
              newV.play().catch(() => { this.isPaused = true })
            }
            setTimeout(() => {
              this.isChangingSource = false
              this.isSwitching = false
            }, 400)
          })
        }, 80)
      } else {
        this.isSwitching = false
        this.isChangingSource = false
      }
    },
    onVideoError() {
      if (this.isChangingSource || !this.videoActive) return
      const v = this.$refs.videoPlayer
      if (!v || !v.error) return
      if (v.error.code <= 2) return

      this.isLoading = false
      this.$message.error('视频资源暂不可用')
    },
    onVideoLoad() { this.isLoading = false },
    onWaiting() { this.isLoading = true },
    onPlaying() { this.isLoading = false; this.isPaused = false },
    handleSingleClick() {
      clearTimeout(this.clickTimer)
      this.clickTimer = setTimeout(() => this.togglePlay(), 220)
    },
    handleDoubleLike(e) {
      clearTimeout(this.clickTimer)
      if (!this.isLiked) this.handleLike()
      this.showLikeAnimation(e)
    },
    showLikeAnimation(e) {
      const box = this.$refs.likeAnimBox
      if (!box) return
      const heart = document.createElement('i')
      heart.className = 'el-icon-star-on heart-anim'
      heart.style.left = `${e.clientX - 35}px`
      heart.style.top = `${e.clientY - 35}px`
      box.appendChild(heart)
      setTimeout(() => heart.remove(), 800)
    },
    togglePlay() {
      const v = this.$refs.videoPlayer
      if (!v) return
      v.paused ? v.play() : v.pause()
      this.isPaused = v.paused
    },
    nextVideo() {
      this.getShortVideoWrapper()
    },
    formatCount(n) {
      return n > 10000 ? (n / 10000).toFixed(1) + 'w' : (n || 0)
    },
    handleWheel(e) {
      if (this.isSwitching || Math.abs(e.deltaY) < 40) return
      this.nextVideo()
    },
    touchStart(e) {
      this.startY = e.touches[0].clientY
    },
    touchEnd(e) {
      const diff = this.startY - e.changedTouches[0].clientY
      if (!this.isSwitching && Math.abs(diff) > 70) {
        this.nextVideo()
      }
    }
  }
}
</script>

<style scoped lang="scss">
.short-video-wrapper {
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100vh;
  background: #000;
  overflow: hidden;
  z-index: 999;
  user-select: none;
  -webkit-user-select: none;
}

.video-bg {
  position: absolute;
  inset: -10%;
  background-size: cover;
  background-position: center;
  filter: blur(30px) brightness(0.25);
  z-index: 0;
}

.video-container {
  position: relative;
  z-index: 1;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.native-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.video-status-overlay {
  position: absolute;
  z-index: 2;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
}
.status-icon {
  font-size: 60px;
  color: rgba(255, 255, 255, 0.6);
}

.video-info-bottom {
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100%;
  background: linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 100%);
  padding: 30px 15px 20px 15px;
  z-index: 10;
  pointer-events: none;
  box-sizing: border-box;

  .info-content {
    margin-right: 75px;
    color: #fff;
    text-align: left;

    .author-name {
      font-size: 16px;
      font-weight: 600;
      margin-bottom: 6px;
      pointer-events: auto;
      cursor: pointer;
    }

    .video-desc {
      font-size: 14px;
      line-height: 1.4;
      color: #fff;
      pointer-events: auto;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 3;
      overflow: hidden;
    }

    .music-tag {
      margin-top: 10px;
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 12px;

      .marquee-box {
        overflow: hidden;
        white-space: nowrap;
        width: 140px;
        .marquee-text {
          display: inline-block;
          animation: marquee 7s linear infinite;
        }
      }
    }
  }
}

@keyframes rotate { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
@keyframes marquee { 0% { transform: translateX(100%); } 100% { transform: translateX(-100%); } }
.music-icon-box { animation: rotate 4s linear infinite; }

.side-bar {
  position: absolute;
  right: 12px;
  bottom: 12vh;
  z-index: 11;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;

  .side-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    cursor: pointer;

    .icon-circle {
      width: 44px;
      height: 44px;
      background: rgba(255, 255, 255, 0.2);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 24px;
      color: #fff;
      backdrop-filter: blur(4px);

      &.is-active {
        color: #fe2c55;
      }
    }

    .count-text {
      font-size: 11px;
      margin-top: 4px;
      color: #fff;
      text-shadow: 0 1px 2px rgba(0,0,0,0.5);
    }
  }

  .author-box {
    .avatar-wrapper {
      border: 2px solid #fff;
      border-radius: 50%;
      position: relative;
      background: #fff;
      display: flex;

      .follow-plus {
        position: absolute;
        bottom: -6px;
        left: 50%;
        transform: translateX(-50%);
        background: #fe2c55;
        border-radius: 50%;
        width: 18px;
        height: 18px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 10px;
        color: #fff;
      }
    }
  }
}

.top-controls {
  position: absolute;
  top: 15px;
  left: 15px;
  z-index: 10;
  display: flex;
  gap: 10px;
}

.like-anim-container {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 5;
}
::v-deep .heart-anim {
  position: absolute;
  font-size: 70px;
  color: #fe2c55;
  animation: heartFly 0.75s ease-out forwards;
}
@keyframes heartFly {
  0% { transform: scale(0.8); opacity: 0.9; }
  100% { transform: translateY(-120px) scale(1.6); opacity: 0; }
}

.user-peek-card {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  justify-content: flex-end;
}
.card-mask {
  flex: 1;
  background: rgba(0, 0, 0, 0.2);
}
.card-content {
  width: 78%;
  height: 100%;
  background: rgba(20, 20, 20, 0.82);
  backdrop-filter: blur(25px) saturate(160%);
  -webkit-backdrop-filter: blur(25px) saturate(160%);
  border-left: 1px solid rgba(255, 255, 255, 0.1);
  color: #fff;
  padding: 60px 20px 30px;
  display: flex;
  flex-direction: column;
  box-shadow: -10px 0 30px rgba(0, 0, 0, 0.6);
  box-sizing: border-box;
}

.slide-fade-enter-active, .slide-fade-leave-active {
  transition: all 0.35s cubic-bezier(0.165, 0.84, 0.44, 1);
}
.slide-fade-enter, .slide-fade-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

.profile-main {
  text-align: center;
  margin-bottom: 25px;

  .card-avatar {
    border: 2px solid #fe2c55;
    margin-bottom: 12px;
    cursor: pointer;
  }
  .name {
    font-size: 16px;
    margin: 6px 0;
    cursor: pointer;
  }
  .bio {
    font-size: 12px;
    color: rgba(255,255,255,0.6);
    margin-bottom: 15px;
    padding: 0 10px;
  }
  .stats-group {
    display: flex;
    justify-content: center;
    gap: 30px;
    margin-bottom: 15px;
    .stat {
      display: flex;
      flex-direction: column;
      b { font-size: 16px; }
      span { font-size: 11px; color: rgba(255,255,255,0.5); }
    }
  }
}

.recent-works {
  .label {
    font-size: 13px;
    color: rgba(255,255,255,0.7);
    margin-bottom: 10px;
  }
  .thumb-list {
    display: flex;
    gap: 8px;
    overflow-x: auto;
    padding-bottom: 4px;
    &::-webkit-scrollbar { display: none; }

    .work-thumb {
      flex: 0 0 31%;
      aspect-ratio: 3/4;
      background-color: rgba(255, 255, 255, 0.1);
      background-size: cover;
      background-position: center;
      background-repeat: no-repeat;
      border-radius: 6px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: rgba(255, 255, 255, 0.6);
      cursor: pointer;

      i {
        font-size: 22px;
        text-shadow: 0 1px 4px rgba(0, 0, 0, 0.6);
      }
    }
  }
}
</style>
